import { SearchOutlined } from "@ant-design/icons";
import { useSearchActivityLogs } from "@entities/activity/lib/hooks";
import { Button } from "@shared/ui";
import { Empty, List } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { ActivityItem } from "../ActivityItem/ActivityItem";
import styles from "./styles.module.scss";

export const ActivityList = ({
  filter,
  searchTerm,
  brandFilter,
  timeFilter,
  briefInfluencerIds,
  datePosition,
  brandOrganisationId,
}: {
  filter?: string;
  searchTerm?: string;
  brandFilter?: string;
  timeFilter?: string;
  briefInfluencerIds?: string[];
  datePosition?: "start" | "end";
  brandOrganisationId?: string;
}) => {
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(10);

  const generateActivityDate = (timeFilter: string) => {
    const now = dayjs();
    switch (timeFilter) {
      case "Today":
        return {
          from: now.startOf("day").toISOString(),
          to: now.endOf("day").toISOString(),
        };
      case "Last 7 Days":
        return {
          from: now.subtract(7, "day").startOf("day").toISOString(),
          to: now.endOf("day").toISOString(),
        };
      case "Current Month":
        return {
          from: now.startOf("month").toISOString(),
          to: now.endOf("day").toISOString(),
        };
      case "All Time":
      default:
        return undefined;
    }
  };

  const { activityLogs, isActivityLogsFetching, refetchActivityLogs } =
    useSearchActivityLogs({
      pageNo,
      limit,
      searchQuery: searchTerm,
      brandIds: brandFilter && brandFilter !== "All" ? [brandFilter] : [],
      types: filter && filter !== "all" ? [filter] : [],
      activityDate: timeFilter ? generateActivityDate(timeFilter) : undefined,
      briefInfluencerIds: briefInfluencerIds,
      brandOrganisationIds: brandOrganisationId
        ? [brandOrganisationId]
        : undefined,
    });

  useEffect(() => {
    refetchActivityLogs();
  }, []);

  useEffect(() => {
    setPageNo(1);
  }, [brandFilter, timeFilter, filter, searchTerm]);

  if (activityLogs?.data?.data?.length === 0 && !isActivityLogsFetching) {
    return (
      <Empty
        image={<SearchOutlined style={{ fontSize: 48, color: "#a3acb9" }} />}
        styles={{
          image: {
            height: "fit-content",
          },
        }}
        style={{
          padding: 40,
        }}
        description={
          <div>
            <h3>No activity found</h3>
            <p>Try adjusting your search or filters.</p>
          </div>
        }
      />
    );
  }

  return (
    <List
      className={styles.activityList}
      itemLayout="horizontal"
      dataSource={activityLogs?.data?.data}
      loading={isActivityLogsFetching}
      renderItem={(item, idx) => (
        <List.Item
          className={styles.listItem}
          style={{
            borderBottom:
              activityLogs?.data?.data &&
              idx !== activityLogs?.data?.data?.length - 1
                ? "1px solid #eee"
                : "none",
          }}
        >
          <ActivityItem activity={item} datePosition={datePosition} />
        </List.Item>
      )}
      pagination={{
        current: pageNo,
        pageSize: limit,
        total: activityLogs?.data?.pagination?.totalCount,
        showSizeChanger: false,
        onChange: (page) => setPageNo(page),
        showTotal: (total) => (
          <span className={styles.paginationText}>Showing {total} events</span>
        ),
        className: styles.paginationBar,
        itemRender: (_page, type, originalElement) => {
          if (type === "prev") {
            return (
              <Button
                size="small"
                className={styles.paginationNav}
                disabled={pageNo <= 1}
                onClick={() => {
                  if (pageNo > 1) {
                    setPageNo(pageNo - 1);
                  }
                }}
              >
                Previous
              </Button>
            );
          }
          if (type === "next") {
            return (
              <Button
                size="small"
                disabled={
                  pageNo >= (activityLogs?.data?.pagination?.totalPages ?? 1)
                }
                className={styles.paginationNav}
                onClick={() => {
                  if (
                    pageNo < (activityLogs?.data?.pagination?.totalCount ?? 1)
                  ) {
                    setPageNo(pageNo + 1);
                  }
                }}
              >
                Next
              </Button>
            );
          }
          return originalElement;
        },
      }}
    />
  );
};
